<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import PageHeader from 'dashboard/components/dahreply/PageHeader.vue';
import PaginationBar from 'dashboard/components/dahreply/PaginationBar.vue';
import DeleteModal from 'dashboard/components/dahreply/DeleteModal.vue';
import LoadingState from '../../../components/widgets/LoadingState.vue';
import PropertyAppointmentModal from './modals/PropertyAppointmentModal.vue';
import RescheduleModal from './modals/RescheduleModal.vue';
import AppointmentEditModal from './modals/AppointmentEditModal.vue';
import CustomerFilter from './components/CustomerFilter.vue';
import { formatTime12h, isProjectDeleted, isFloorPlanDeleted } from './helpers';
import { apiErrorMessage } from '../../../api/realEstate/errors';
import ContactAPI from '../../../api/contacts';
import ConversationAPI from '../../../api/inbox/conversation';
import { getInboxClassByType } from '../../../helper/inbox';
import {
  APPT_TYPES,
  APPT_STATUSES,
  APPT_STATUS_STYLE,
  FIELD_CLASS,
  SELECT_CLASS,
} from './constants';

const PAGE_SIZE = 20;

// Which status-change buttons show for a row, keyed by the appointment's
// current status — follows the PRD §6 normal path (not a hard state
// machine: PUT …/status accepts any transition, this just decides what's
// offered from this table). completed/cancelled are terminal — no buttons.
const APPT_ACTIONS_BY_STATUS = {
  requested: ['confirm', 'reschedule', 'cancel'],
  confirmed: ['reschedule', 'complete', 'cancel'],
  rescheduled: ['confirm', 'reschedule', 'complete', 'cancel'],
  completed: [],
  cancelled: [],
};

// `status_counts` (for the tab badges) applies every filter except
// `status`/`view` itself, so it's already correct for every tab at once —
// docs/real-estate/be/10-api-reference.md §A13.
const TABS = [
  {
    id: 'upcoming',
    labelKey: 'REAL_ESTATE.APPOINTMENTS.TAB_UPCOMING',
    view: 'upcoming',
  },
  {
    id: 'requested',
    labelKey: 'REAL_ESTATE.APPOINTMENTS.TAB_REQUESTED',
    view: 'upcoming',
    status: 'requested',
    countKey: 'requested',
  },
  {
    id: 'confirmed',
    labelKey: 'REAL_ESTATE.APPOINTMENTS.TAB_CONFIRMED',
    view: 'upcoming',
    status: 'confirmed',
    countKey: 'confirmed',
  },
  {
    id: 'rescheduled',
    labelKey: 'REAL_ESTATE.APPOINTMENTS.TAB_RESCHEDULED',
    view: 'upcoming',
    status: 'rescheduled',
    countKey: 'rescheduled',
  },
  {
    id: 'archive',
    labelKey: 'REAL_ESTATE.APPOINTMENTS.TAB_ARCHIVE',
    view: 'archive',
  },
];

export default {
  name: 'PropertyAppointments',
  components: {
    PageHeader,
    PaginationBar,
    DeleteModal,
    LoadingState,
    PropertyAppointmentModal,
    RescheduleModal,
    AppointmentEditModal,
    CustomerFilter,
  },
  data() {
    return {
      isLoading: false,
      tabs: TABS,
      activeTab: 'upcoming',
      // Lets other pages (e.g. a floor plan's "has upcoming appointments"
      // message) deep-link here already filtered to one project.
      filterProperty: this.$route.query.property || '',
      filterType: '',
      filterAgent: '',
      // { id, name } of the picked Chatwoot contact, or null.
      filterCustomer: null,
      // A full channel type, e.g. 'Channel::Whatsapp' — the API matches it
      // against the booking's conversation inbox.
      filterChannel: '',
      // Inclusive YYYY-MM-DD bounds (start_date/end_date on the API).
      filterFrom: '',
      filterTo: '',
      page: 1,
      pageSize: PAGE_SIZE,
      apptTypes: APPT_TYPES,
      apptStatuses: APPT_STATUSES,
      apptStatusStyle: APPT_STATUS_STYLE,
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      isCreateModalOpen: false,
      editingAppointment: null,
      reschedulingAppointment: null,
      appointmentToDelete: null,
      isDeleting: false,
      // Looked up per row, since the appointment carries neither: the
      // contact's email ({ [customer_id]: { email, phone_number } }) and the
      // conversation's inbox ({ [conversation_id]: inbox_id }). null = not
      // loaded (yet, or the lookup failed).
      contactDetails: {},
      conversationInboxes: {},
    };
  },
  computed: {
    ...mapGetters('realEstateAppointments', {
      appointments: 'getResults',
      total: 'getTotal',
      statusCounts: 'getStatusCounts',
    }),
    ...mapGetters('realEstate', { projects: 'getProjectOptions' }),
    ...mapGetters('realEstateAgents', { agents: 'getAgents' }),
    ...mapGetters('inboxes', { getInbox: 'getInbox', inboxes: 'getInboxes' }),
    // One option per channel type this account has an inbox for.
    channelOptions() {
      const types = [];
      this.inboxes.forEach(inbox => {
        if (inbox.channel_type && !types.includes(inbox.channel_type)) {
          types.push(inbox.channel_type);
        }
      });
      return types
        .map(type => ({ value: type, label: this.channelTypeLabel(type) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pagination() {
      return {
        currentPage: this.page,
        totalPages: this.totalPages,
        pageSize: this.pageSize,
      };
    },
    isDateRangeInvalid() {
      return !!(
        this.filterFrom &&
        this.filterTo &&
        this.filterTo < this.filterFrom
      );
    },
    hasActiveFilters() {
      return !!(
        this.filterProperty ||
        this.filterType ||
        this.filterAgent ||
        this.filterCustomer ||
        this.filterChannel ||
        this.filterFrom ||
        this.filterTo
      );
    },
    // Everything that narrows the list except the page — one watcher on
    // this resets to page 1 and refetches once, however many filters change
    // together (e.g. Clear filters).
    filterQuery() {
      const tab = this.tabs.find(t => t.id === this.activeTab);
      const query = { view: tab.view };
      if (tab.status) query.status = tab.status;
      if (this.filterProperty) query.property_id = this.filterProperty;
      if (this.filterType) query.appointment_type = this.filterType;
      if (this.filterAgent) query.agent_id = this.filterAgent;
      if (this.filterCustomer) query.customer_id = this.filterCustomer.id;
      if (this.filterChannel) query.channel = this.filterChannel;
      if (!this.isDateRangeInvalid) {
        if (this.filterFrom) query.start_date = this.filterFrom;
        if (this.filterTo) query.end_date = this.filterTo;
      }
      return query;
    },
    listQuery() {
      return {
        ...this.filterQuery,
        page: this.page,
        page_size: this.pageSize,
      };
    },
  },
  watch: {
    filterQuery() {
      // Changing `page` triggers its own watcher's load; only load here
      // when already on page 1, so a filter change never fetches twice.
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.loadAppointments();
      }
    },
    page() {
      this.loadAppointments();
    },
    appointments() {
      this.loadRowDetails();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions('realEstate', ['fetchProjectOptions']),
    ...mapActions('realEstateAgents', ['fetchAgents']),
    ...mapActions('realEstateAppointments', [
      'fetchAppointments',
      'setAppointmentStatus',
      'deleteAppointment',
    ]),
    async loadData() {
      this.isLoading = true;
      try {
        await Promise.all([
          this.fetchProjectOptions(),
          this.fetchAgents(),
          this.loadAppointments(),
        ]);
      } finally {
        this.isLoading = false;
      }
    },
    async loadAppointments() {
      this.isLoading = true;
      try {
        await this.fetchAppointments(this.listQuery);
      } finally {
        this.isLoading = false;
      }
    },
    // Each contact and conversation is looked up once per page visit.
    // Failures (deleted contact, an inbox the user can't access) just leave
    // the extra details out.
    loadRowDetails() {
      const customerIds = new Set();
      const conversationIds = new Set();
      this.appointments.forEach(appointment => {
        const id = this.customerId(appointment);
        if (id && !(id in this.contactDetails)) customerIds.add(id);
        const conversationId = appointment.conversation_id;
        if (conversationId && !(conversationId in this.conversationInboxes)) {
          conversationIds.add(conversationId);
        }
      });
      customerIds.forEach(async id => {
        this.$set(this.contactDetails, id, null);
        try {
          const { data } = await ContactAPI.show(id);
          const contact = data.payload || {};
          this.$set(this.contactDetails, id, {
            email: contact.email || '',
            phone_number: contact.phone_number || '',
          });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
      conversationIds.forEach(async id => {
        this.$set(this.conversationInboxes, id, null);
        try {
          const { data } = await ConversationAPI.show(id);
          this.$set(this.conversationInboxes, id, data.inbox_id || null);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    },
    customerId(appointment) {
      return appointment.customer ? appointment.customer.customer_id : null;
    },
    customerPhone(appointment) {
      const details = this.contactDetails[this.customerId(appointment)];
      return (
        (appointment.customer && appointment.customer.phone_number) ||
        (details && details.phone_number) ||
        ''
      );
    },
    customerEmail(appointment) {
      const details = this.contactDetails[this.customerId(appointment)];
      return (details && details.email) || '';
    },
    contactRoute(appointment) {
      return {
        name: 'contact_profile_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          contactId: String(this.customerId(appointment)),
        },
      };
    },
    channelTypeLabel(channelType) {
      const key = `REAL_ESTATE.APPOINTMENTS.CHANNELS.${String(
        channelType
      ).replace('Channel::', '')}`;
      return this.$te(key) ? this.$t(key) : channelType;
    },
    // { icon, label, inboxName } of the conversation's channel, or null when
    // the booking has no conversation or its inbox isn't known.
    channelOf(appointment) {
      const inboxId = this.conversationInboxes[appointment.conversation_id];
      if (!inboxId) return null;
      const inbox = this.getInbox(inboxId);
      if (!inbox || !inbox.id) return null;
      const type = String(inbox.channel_type || '').replace('Channel::', '');
      const key =
        type === 'TwilioSms' && inbox.medium === 'whatsapp' ? 'Whatsapp' : type;
      const labelKey = `REAL_ESTATE.APPOINTMENTS.CHANNELS.${key}`;
      return {
        icon: getInboxClassByType(inbox.channel_type, inbox.phone_number),
        label: this.$te(labelKey) ? this.$t(labelKey) : inbox.name,
        inboxName: inbox.name,
      };
    },
    goToConversation(appointment) {
      this.$router.push({
        name: 'inbox_conversation',
        params: {
          accountId: this.$route.params.accountId,
          conversation_id: appointment.conversation_id,
        },
      });
    },
    clearFilters() {
      this.filterProperty = '';
      this.filterType = '';
      this.filterAgent = '';
      this.filterCustomer = null;
      this.filterChannel = '';
      this.filterFrom = '';
      this.filterTo = '';
    },
    tabCount(tab) {
      return tab.countKey ? this.statusCounts[tab.countKey] : null;
    },
    isWalkIn(appointment) {
      return appointment.agent === null && appointment.end_time === null;
    },
    rowActions(appointment) {
      return APPT_ACTIONS_BY_STATUS[appointment.status] || [];
    },
    hasAction(appointment, action) {
      return this.rowActions(appointment).includes(action);
    },
    isProjectDeleted,
    isFloorPlanDeleted,
    appointmentTime(appointment) {
      return this.isWalkIn(appointment)
        ? this.$t('REAL_ESTATE.APPOINTMENTS.ARRIVE_AT', {
            time: formatTime12h(appointment.start_time),
          })
        : formatTime12h(appointment.start_time);
    },
    deleteDescription(appointment) {
      if (!appointment) return '';
      const type = this.apptTypes[appointment.appointment_type];
      const property = appointment.property.name;
      return `${type} · ${property} · ${
        appointment.date
      } ${this.appointmentTime(appointment)}`;
    },
    isChecklistComplete(appointment) {
      return !!(
        appointment.handover_checklist &&
        appointment.handover_checklist.payment_proof_received &&
        appointment.handover_checklist.signed_agreement_on_file
      );
    },
    handlePageSizeChange(size) {
      this.pageSize = size;
      this.page = 1;
      this.loadAppointments();
    },
    openCreateModal() {
      this.isCreateModalOpen = true;
    },
    closeCreateModal(result) {
      this.isCreateModalOpen = false;
      if (result && result.created) this.loadAppointments();
    },
    openEditModal(appointment) {
      this.editingAppointment = appointment;
    },
    closeEditModal() {
      this.editingAppointment = null;
      this.loadAppointments();
    },
    openRescheduleModal(appointment) {
      this.reschedulingAppointment = appointment;
    },
    closeRescheduleModal() {
      this.reschedulingAppointment = null;
      this.loadAppointments();
    },
    handleDeleteClick(appointment) {
      this.appointmentToDelete = appointment;
    },
    async confirmDelete() {
      this.isDeleting = true;
      try {
        await this.deleteAppointment(this.appointmentToDelete.appointment_id);
        useAlert(this.$t('REAL_ESTATE.APPOINTMENTS.SUCCESS_DELETED'));
        this.appointmentToDelete = null;
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        this.isDeleting = false;
      }
    },
    async changeStatus(appointment, status, reason = null) {
      const statusSuccessKeys = {
        confirmed: 'REAL_ESTATE.APPOINTMENTS.SUCCESS_CONFIRMED',
        completed: 'REAL_ESTATE.APPOINTMENTS.SUCCESS_COMPLETED',
        cancelled: 'REAL_ESTATE.APPOINTMENTS.SUCCESS_CANCELLED',
      };
      try {
        await this.setAppointmentStatus({
          id: appointment.appointment_id,
          status,
          reason,
        });
        useAlert(
          this.$t(
            statusSuccessKeys[status] ||
              'REAL_ESTATE.APPOINTMENTS.SUCCESS_UPDATED'
          )
        );
      } catch (err) {
        useAlert(apiErrorMessage(this, err));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    },
    handleConfirm(appointment) {
      return this.changeStatus(appointment, 'confirmed');
    },
    handleComplete(appointment) {
      return this.changeStatus(appointment, 'completed');
    },
    handleCancel(appointment) {
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        this.$t('REAL_ESTATE.APPOINTMENTS.CANCEL_REASON_PROMPT')
      );
      if (reason === null) return null;
      return this.changeStatus(appointment, 'cancelled', reason);
    },
  },
};
</script>

<template>
  <div class="p-4 md:p-8 h-full flex flex-col overflow-hidden flex-1 min-w-0">
    <PropertyAppointmentModal
      v-if="isCreateModalOpen"
      @close="closeCreateModal"
    />
    <RescheduleModal
      v-if="reschedulingAppointment"
      :appointment="reschedulingAppointment"
      @close="closeRescheduleModal"
    />
    <AppointmentEditModal
      v-if="editingAppointment"
      :appointment="editingAppointment"
      @close="closeEditModal"
    />
    <DeleteModal
      v-if="appointmentToDelete"
      :delete-name="deleteDescription(appointmentToDelete)"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
      @close="appointmentToDelete = null"
    />

    <PageHeader
      :title="$t('REAL_ESTATE.APPOINTMENTS.HEADER_TITLE')"
      icon="calendar"
      :description="$t('REAL_ESTATE.APPOINTMENTS.HEADER_DESCRIPTION')"
    >
      <template #actions>
        <woot-button icon="add" @click="openCreateModal">
          {{ $t('REAL_ESTATE.APPOINTMENTS.NEW_BUTTON') }}
        </woot-button>
      </template>
    </PageHeader>

    <nav
      class="flex flex-wrap gap-4 mt-2 border-b border-slate-200 dark:border-slate-700"
    >
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="pb-2 text-sm font-medium border-b-2 flex items-center gap-1.5"
        :class="
          activeTab === tab.id
            ? 'border-woot-500 text-woot-600 dark:text-woot-400'
            : 'border-transparent text-slate-500 dark:text-slate-400'
        "
        @click="activeTab = tab.id"
      >
        {{ $t(tab.labelKey) }}
        <span
          v-if="tabCount(tab)"
          class="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"
        >
          {{ tabCount(tab) }}
        </span>
      </button>
    </nav>

    <div
      class="mt-4 flex flex-col md:flex-row flex-wrap gap-3 items-stretch md:items-center"
    >
      <select
        v-model="filterProperty"
        :class="selectClass"
        class="max-w-[220px]"
      >
        <option value="">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_ALL_PROPERTIES') }}
        </option>
        <option
          v-for="project in projects"
          :key="project.property_id"
          :value="project.property_id"
        >
          {{ project.name }}
        </option>
      </select>
      <select v-model="filterType" :class="selectClass" class="max-w-[200px]">
        <option value="">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_ALL_TYPES') }}
        </option>
        <option v-for="(label, key) in apptTypes" :key="key" :value="key">
          {{ label }}
        </option>
      </select>
      <select v-model="filterAgent" :class="selectClass" class="max-w-[200px]">
        <option value="">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_ALL_AGENTS') }}
        </option>
        <option
          v-for="agent in agents"
          :key="agent.agent_id"
          :value="agent.agent_id"
        >
          {{ agent.name }}
        </option>
      </select>
      <CustomerFilter v-model="filterCustomer" />
      <select
        v-model="filterChannel"
        :class="selectClass"
        class="max-w-[200px]"
        :aria-label="$t('REAL_ESTATE.APPOINTMENTS.COLUMN_CHANNEL')"
      >
        <option value="">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_ALL_CHANNELS') }}
        </option>
        <option
          v-for="option in channelOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <div class="flex items-center gap-2">
        <label class="mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_FROM') }}
        </label>
        <input
          v-model="filterFrom"
          type="date"
          class="reset-base mb-0 !w-40"
          :class="fieldClass"
        />
        <label class="mb-0 text-sm text-slate-500 dark:text-slate-400">
          {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_TO') }}
        </label>
        <input
          v-model="filterTo"
          type="date"
          class="reset-base mb-0 !w-40"
          :class="[fieldClass, { '!border-red-500': isDateRangeInvalid }]"
        />
      </div>
      <woot-button
        v-if="hasActiveFilters"
        variant="clear"
        color-scheme="secondary"
        size="small"
        icon="dismiss"
        class="shrink-0"
        @click="clearFilters"
      >
        {{ $t('REAL_ESTATE.LIST.CLEAR_FILTERS') }}
      </woot-button>
    </div>
    <p v-if="isDateRangeInvalid" class="mt-1 mb-0 text-xs text-red-500">
      {{ $t('REAL_ESTATE.APPOINTMENTS.FILTER_DATE_RANGE_INVALID') }}
    </p>

    <div class="flex-1 overflow-y-auto">
      <LoadingState
        v-if="isLoading"
        :message="$t('REAL_ESTATE.APPOINTMENTS.LOADING')"
      />
      <div
        v-else-if="appointments.length === 0"
        class="flex flex-col items-center justify-center py-12"
      >
        <fluent-icon
          icon="calendar"
          class="text-slate-300 dark:text-slate-600 mb-4"
          size="48"
        />
        <p class="text-slate-500 dark:text-slate-400 text-center">
          {{ $t('REAL_ESTATE.APPOINTMENTS.EMPTY_STATE') }}
        </p>
      </div>
      <table v-else class="w-full text-sm border-collapse">
        <thead>
          <tr
            class="text-left text-slate-400 border-b border-slate-200 dark:border-slate-700"
          >
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_PROPERTY') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_CUSTOMER') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_CHANNEL') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_TYPE') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_FLOOR_PLAN') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_AGENT') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_DATE') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_DURATION') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_STATUS') }}
            </th>
            <th class="py-2 pr-4 font-medium">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_CHECKLIST') }}
            </th>
            <th class="py-2 pr-4 font-medium text-right">
              {{ $t('REAL_ESTATE.APPOINTMENTS.COLUMN_ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="appointment in appointments"
            :key="appointment.appointment_id"
            class="border-b border-slate-100 dark:border-slate-800"
          >
            <td class="py-2 pr-4 text-slate-900 dark:text-slate-100">
              {{ appointment.property.name }}
              <span
                v-if="isProjectDeleted(appointment)"
                class="ml-1 inline-flex px-1.5 rounded text-[11px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.DELETED_TAG') }}
              </span>
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              <template v-if="customerId(appointment)">
                <router-link
                  :to="contactRoute(appointment)"
                  class="font-medium text-slate-900 dark:text-slate-100 hover:text-woot-600 dark:hover:text-woot-400 hover:underline"
                >
                  {{
                    appointment.customer.name ||
                    $t('REAL_ESTATE.APPOINTMENTS.CUSTOMER_UNNAMED')
                  }}
                </router-link>
                <p
                  v-if="customerPhone(appointment)"
                  class="mb-0 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap"
                >
                  {{ customerPhone(appointment) }}
                </p>
                <p
                  v-if="customerEmail(appointment)"
                  class="mb-0 text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]"
                  :title="customerEmail(appointment)"
                >
                  {{ customerEmail(appointment) }}
                </p>
              </template>
              <template v-else>—</template>
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              <span
                v-if="channelOf(appointment)"
                class="inline-flex items-center gap-1.5 whitespace-nowrap"
                :title="channelOf(appointment).inboxName"
              >
                <fluent-icon :icon="channelOf(appointment).icon" size="14" />
                {{ channelOf(appointment).label }}
              </span>
              <template v-else>—</template>
            </td>
            <td class="py-2 pr-4">
              <span
                v-if="appointment.appointment_type === 'key_handover'"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-woot-50 dark:bg-woot-900/30 text-woot-600 dark:text-woot-400"
              >
                {{ apptTypes[appointment.appointment_type] }}
              </span>
              <span v-else class="text-slate-600 dark:text-slate-300">
                {{ apptTypes[appointment.appointment_type] }}
              </span>
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ appointment.unit ? appointment.unit.floor_plan_code : '—' }}
              <span
                v-if="isFloorPlanDeleted(appointment)"
                class="ml-1 inline-flex px-1.5 rounded text-[11px] font-medium bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.DELETED_TAG') }}
              </span>
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ appointment.agent ? appointment.agent.name : '—' }}
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{ appointment.date }}
              {{ appointmentTime(appointment) }}
            </td>
            <td class="py-2 pr-4 text-slate-600 dark:text-slate-300">
              {{
                isWalkIn(appointment)
                  ? $t('REAL_ESTATE.APPOINTMENTS.OPEN_DURATION')
                  : $t('REAL_ESTATE.APPOINTMENTS.MINUTES', {
                      minutes: appointment.duration_minutes,
                    })
              }}
            </td>
            <td class="py-2 pr-4">
              <span
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                :class="apptStatusStyle[appointment.status]"
              >
                {{ apptStatuses[appointment.status] }}
              </span>
            </td>
            <td class="py-2 pr-4">
              <span
                v-if="appointment.appointment_type === 'key_handover'"
                class="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium text-center leading-tight"
                :class="
                  isChecklistComplete(appointment)
                    ? apptStatusStyle.confirmed
                    : apptStatusStyle.requested
                "
              >
                {{
                  isChecklistComplete(appointment)
                    ? $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_COMPLETE')
                    : $t('REAL_ESTATE.APPOINTMENTS.CHECKLIST_PENDING')
                }}
              </span>
              <span v-else>—</span>
            </td>
            <td class="py-2 pr-4 text-right whitespace-nowrap">
              <woot-button
                v-if="hasAction(appointment, 'confirm')"
                variant="smooth"
                size="tiny"
                @click="handleConfirm(appointment)"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.CONFIRM') }}
              </woot-button>
              <woot-button
                v-if="hasAction(appointment, 'reschedule')"
                variant="smooth"
                size="tiny"
                :is-disabled="isProjectDeleted(appointment)"
                :title="
                  isProjectDeleted(appointment)
                    ? $t('REAL_ESTATE.APPOINTMENTS.RESCHEDULE_PROJECT_DELETED')
                    : ''
                "
                @click="openRescheduleModal(appointment)"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.RESCHEDULE') }}
              </woot-button>
              <woot-button
                v-if="hasAction(appointment, 'complete')"
                variant="smooth"
                size="tiny"
                @click="handleComplete(appointment)"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.COMPLETE') }}
              </woot-button>
              <woot-button
                v-if="hasAction(appointment, 'cancel')"
                variant="smooth"
                size="tiny"
                color-scheme="alert"
                @click="handleCancel(appointment)"
              >
                {{ $t('REAL_ESTATE.APPOINTMENTS.CANCEL_ACTION') }}
              </woot-button>
              <woot-button
                variant="clear"
                size="small"
                icon="chat"
                :is-disabled="!appointment.conversation_id"
                :title="
                  appointment.conversation_id
                    ? $t('REAL_ESTATE.APPOINTMENTS.GO_TO_CONVERSATION')
                    : $t('REAL_ESTATE.APPOINTMENTS.NO_CONVERSATION')
                "
                @click="goToConversation(appointment)"
              />
              <woot-button
                variant="clear"
                size="small"
                icon="edit"
                @click="openEditModal(appointment)"
              />
              <woot-button
                variant="clear"
                size="small"
                color-scheme="alert"
                icon="delete"
                @click="handleDeleteClick(appointment)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <PaginationBar
        v-if="appointments.length"
        :pagination="pagination"
        :loading="isLoading"
        :page-size-options="[10, 20, 50]"
        @previous="page -= 1"
        @next="page += 1"
        @page-size-change="handlePageSizeChange"
      />
    </div>
  </div>
</template>
